import { useMemo } from 'react';
import {
  Box,
  Chip,
  LinearProgress,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { SystemJob } from 'src/Api/exchanges';

interface JobProgressStepperProps {
  job: SystemJob;
  /**
   * Noun shown after "Starting"/"Stopping" — e.g. "Unwinder", "Orderbooks",
   * "Everything".
   */
  label: string;
}

/**
 * Shared step-by-step progress view for an on/off `SystemJob`: a header with a
 * completion icon, a determinate progress bar while running, the vertical MUI
 * stepper, and the job-level error. Renders bare content (no Paper, no
 * positioning) so callers can drop it into a popover or a Dialog.
 *
 * Extracted from StopStartUnwinderButtons + ToggleUpdatesButton (which had
 * identical inline copies) and reused by the master StopAllButton.
 */
export const JobProgressStepper = ({ job, label }: JobProgressStepperProps) => {
  const progress = useMemo(() => {
    if (!job.steps.length) return 0;
    const completed = job.steps.filter(s => s.status === 'completed').length;
    return Math.round((completed / job.steps.length) * 100);
  }, [job]);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 0.5
        }}
      >
        <Typography
          variant="caption"
          fontWeight={600}
          sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
        >
          {job.type === 'start' ? 'Starting' : 'Stopping'} {label}
        </Typography>
        {job.status === 'completed' && (
          <CheckCircleOutlineIcon
            sx={{ fontSize: 16, color: 'success.main' }}
          />
        )}
        {job.status === 'failed' && (
          <ErrorOutlineIcon sx={{ fontSize: 16, color: 'error.main' }} />
        )}
      </Box>

      {job.status === 'running' && (
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{ mb: 1, borderRadius: 1, height: 3 }}
        />
      )}

      <Stepper
        activeStep={job.currentStep}
        orientation="vertical"
        sx={{
          '& .MuiStepConnector-line': { minHeight: 8 },
          '& .MuiStepLabel-label': { fontSize: '0.7rem' },
          '& .MuiStepLabel-iconContainer': { pr: 1 },
          '& .MuiSvgIcon-root': { fontSize: '0.9rem' }
        }}
      >
        {job.steps.map((step, idx) => (
          <Step key={idx} completed={step.status === 'completed'}>
            <StepLabel error={step.status === 'failed'} sx={{ py: 0 }}>
              {step.label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      {job.error && (
        <Typography
          variant="caption"
          color="error"
          sx={{ mt: 0.5, display: 'block' }}
        >
          {job.error}
        </Typography>
      )}
    </>
  );
};

/**
 * Result-snackbar model derived from a completed/failed STOP|START|PANIC job.
 * Carries the enriched order-cancel counts the control plane now returns so the
 * snackbar can show "X cancelled, Y still resting" and flag an incomplete cancel.
 */
export interface JobResultSnack {
  kind: 'success' | 'error';
  /** e.g. "Opener stop completed (panic was triggered)." */
  headline: string;
  triggeredPanic: boolean;
  kalshiCancelled?: number;
  polymarketCancelled?: number;
  kalshiRemaining?: number;
  polymarketRemaining?: number;
  podForceDeleted?: boolean;
  /** Total orders still resting after the cancel sweep ( > 0 = incomplete). */
  remaining: number;
}

const POLYMARKET_PROXY_CAVEAT =
  'Las órdenes de la UI / proxy-wallet de Polymarket NO se cancelan por panic.';

/**
 * Build the snackbar model from a finished `SystemJob`. `label` is the
 * human noun for the subsystem ("Opener" / "Unwinder"). Shared by both
 * StopStart{Opener,Unwinder}Buttons so the two stay mirror-identical.
 */
export const buildJobResultSnack = (
  label: string,
  job: SystemJob
): JobResultSnack => {
  const verb = job.type === 'stop' ? 'stop' : 'start';
  const triggeredPanic = Boolean(job.triggeredPanic);

  if (job.status === 'failed') {
    return {
      kind: 'error',
      headline: `${label} ${verb} failed: ${job.error ?? 'unknown error'}`,
      triggeredPanic,
      remaining: 0
    };
  }

  const kalshiRemaining = job.kalshiRemaining ?? 0;
  const polymarketRemaining = job.polymarketRemaining ?? 0;
  const remaining = kalshiRemaining + polymarketRemaining;

  return {
    kind: remaining > 0 ? 'error' : 'success',
    headline: `${label} ${verb} completed${
      triggeredPanic ? ' (panic was triggered)' : ''
    }.`,
    triggeredPanic,
    kalshiCancelled: job.kalshiCancelled,
    polymarketCancelled: job.polymarketCancelled,
    kalshiRemaining: job.kalshiRemaining,
    polymarketRemaining: job.polymarketRemaining,
    podForceDeleted: job.podForceDeleted,
    remaining
  };
};

/**
 * Snackbar body for a finished STOP/PANIC job: the headline plus the enriched
 * cancel counts, a prominent warning when orders are still resting, and the
 * Polymarket proxy-wallet caveat whenever a cancel sweep ran.
 */
export const JobResultContent = ({ snack }: { snack: JobResultSnack }) => {
  const hasCounts =
    snack.kalshiCancelled !== undefined ||
    snack.polymarketCancelled !== undefined ||
    snack.kalshiRemaining !== undefined ||
    snack.polymarketRemaining !== undefined;

  return (
    <Box>
      <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
        {snack.headline}
      </Typography>

      {hasCounts && (
        <Stack
          direction="row"
          spacing={0.5}
          flexWrap="wrap"
          useFlexGap
          sx={{ mt: 0.5 }}
        >
          <Chip
            size="small"
            color="default"
            variant="outlined"
            label={`Kalshi: ${snack.kalshiCancelled ?? 0} cancelled`}
            sx={{ fontSize: '0.62rem', height: 18 }}
          />
          <Chip
            size="small"
            color="default"
            variant="outlined"
            label={`Polymarket: ${snack.polymarketCancelled ?? 0} cancelled`}
            sx={{ fontSize: '0.62rem', height: 18 }}
          />
        </Stack>
      )}

      {snack.remaining > 0 && (
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          sx={{ mt: 0.5 }}
        >
          <WarningAmberIcon sx={{ fontSize: 16, color: 'error.main' }} />
          <Typography
            variant="caption"
            color="error"
            sx={{ fontWeight: 700 }}
          >
            CANCEL INCOMPLETE — still resting: Kalshi{' '}
            {snack.kalshiRemaining ?? 0}, Polymarket{' '}
            {snack.polymarketRemaining ?? 0}
          </Typography>
        </Stack>
      )}

      {snack.podForceDeleted && (
        <Typography
          variant="caption"
          sx={{ mt: 0.5, display: 'block', color: 'warning.main' }}
        >
          Engine pod was force-deleted before scaling to 0.
        </Typography>
      )}

      {(hasCounts || snack.triggeredPanic) && (
        <Typography
          variant="caption"
          sx={{
            mt: 0.5,
            display: 'block',
            fontStyle: 'italic',
            opacity: 0.85
          }}
        >
          {POLYMARKET_PROXY_CAVEAT}
        </Typography>
      )}
    </Box>
  );
};
